import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { WaterDrop, WbSunny, AcUnit, Air, Grass } from "@mui/icons-material";
import CoronavirusIcon from "@mui/icons-material/Coronavirus";
import { useTranslate } from "../../Translate/TranslateContext";
import ManualSidebarNav from "../Blocks/ManualSidebarNav";
import ManualDefinition from "../Blocks/ManualDefinition";

export default function MicroclimateRisksManual() {
  const { translate } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeSection, setActiveSection] = useState("disease_advantages");

  const sectionRefs = {
    disease_advantages: useRef(null),
    water_stress_advantages: useRef(null),
    heat_advantages: useRef(null),
    cold_advantages: useRef(null),
    soil_advantages: useRef(null),
    airflow_advantages: useRef(null),
    disease_risks: useRef(null),
    water_stress_risks: useRef(null),
    heat_risks: useRef(null),
    cold_risks: useRef(null),
    soil_risks: useRef(null),
    airflow_risks: useRef(null),
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  const jumpToSection = (id) => {
    const node = sectionRefs[id]?.current;
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sections = [
    {
      id: "disease_advantages",
      label: translate("disease_advantages"),
      icon: <CoronavirusIcon fontSize="small" />,
      items: [
        "reduced_humidity_pressure",
        "dry_canopy_environment",
        "rapid_surface_drying",
        "reduced_fungal_pressure",
        "clean_canopy_conditions",
        "ventilated_canopy_environment",
        "rapid_canopy_drying",
        "naturally_clean_canopy_environment",
      ],
    },
    {
      id: "water_stress_advantages",
      label: translate("water_stress_advantages"),
      icon: <WaterDrop fontSize="small" />,
      items: [
        "balanced_vapor_demand",
        "balanced_atmospheric_demand",
        "reduced_evaporative_loss",
      ],
    },
    {
      id: "heat_advantages",
      label: translate("heat_advantages"),
      icon: <WbSunny fontSize="small" />,
      items: [
        "high_light_availability",
        "strong_heat_resource",
        "strong_heat_accumulation",
        "resource_supported_growth_environment",
        "high_seasonal_growth_capacity",
      ],
    },
    {
      id: "cold_advantages",
      label: translate("cold_advantages"),
      icon: <AcUnit fontSize="small" />,
      items: [
        "extended_growth_window",
        "extended_growing_season",
        "frost_protected_environment",
        "thermal_stability",
      ],
    },
    {
      id: "soil_advantages",
      label: translate("soil_advantages"),
      icon: <Grass fontSize="small" />,
      items: [
        "strong_surface_water_availability",
        "strong_mid_rootzone_reserve",
        "strong_deep_water_reserve",
        "well_supplied_upper_rootzone",
        "well_supplied_lower_rootzone",
        "strong_rootzone_water_reserve",
        "balanced_rootzone_moisture",
        "strong_soil_recharge",
        "low_storm_pressure",
        "gentle_precipitation_environment",
      ],
    },
    {
      id: "airflow_advantages",
      label: translate("airflow_advantages"),
      icon: <Air fontSize="small" />,
      items: [
        "strong_air_movement",
        "stable_airflow_conditions",
        "microclimate_stability",
      ],
    },
    {
      id: "disease_risks",
      label: translate("disease_risks"),
      icon: <CoronavirusIcon fontSize="small" />,
      items: [
        "higher_fungal_pressure",
        "slow_canopy_drying",
        "canopy_moisture_trapping",
        "warm_humid_growth_environment",
      ],
    },
    {
      id: "water_stress_risks",
      label: translate("water_stress_risks"),
      icon: <WaterDrop fontSize="small" />,
      items: [
        "higher_water_loss",
        "high_transpiration_stress",
        "atmospheric_drying_pressure",
        "excessive_evaporative_demand",
        "persistent_atmospheric_demand",
        "crop_desiccation_pressure",
        "surface_drying_pressure",
        "rapid_surface_depletion",
      ],
    },
    {
      id: "heat_risks",
      label: translate("heat_risks"),
      icon: <WbSunny fontSize="small" />,
      items: [
        "higher_heat_stress",
        "persistent_heat_loading",
        "canopy_overheating_risk",
      ],
    },
    {
      id: "cold_risks",
      label: translate("cold_risks"),
      icon: <AcUnit fontSize="small" />,
      items: [
        "cold_shock_risk",
        "frost_damage_risk",
        "freeze_thaw_instability",
        "temperature_swing_stress",
      ],
    },
    {
      id: "soil_risks",
      label: translate("soil_risks"),
      icon: <Grass fontSize="small" />,
      items: [
        "persistent_rootzone_wetness",
        "episodic_saturation_pressure",
        "storm_driven_saturation",
        "soil_recharge_limitation",
        "rootzone_moisture_stratification",
        "high_water_turnover_environment",
      ],
    },
    {
      id: "airflow_risks",
      label: translate("airflow_risks"),
      icon: <Air fontSize="small" />,
      items: [
        "wind_exposure_stress",
        "unstable_airflow_conditions",
        "microclimate_volatility",
      ],
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 0 : 5 }}>
      <Box sx={{ position: isMobile ? "sticky" : "static", top: 0, zIndex: 1, bgcolor: "background.default", mb: isMobile ? 3 : 0 }}>
        <ManualSidebarNav
          sections={sections.map((s) => ({
            id: s.id,
            label: s.label,
            icon: s.icon,
          }))}
          activeSection={activeSection}
          onJump={jumpToSection}
        />
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: isMobile ? 4 : 6 }}>
        {sections.map((section) => (
          <Box key={section.id} id={section.id} ref={sectionRefs[section.id]}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
              {section.label}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {section.items.map((item) => (
                <ManualDefinition
                  key={item}
                  title={translate(item)}
                  definition={translate(`${item}_definition`)}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}